// @flow

import LogToServer from 'app/common/lib/LogToServer';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import typeof { resources } from 'node/resources/routes';

const DEFAULT_FLUSH_TIMEOUT_DELAY = 5000;

type QueueMapper<T> = (queue: Array<T>) => any;

class QueuedCall<ResourcePayload> {
  _flushTimeoutDelay: number;
  _flushTimer: ?TimeoutID = null;
  _listeningForWindowClose: boolean = false;
  _queue: Array<ResourcePayload> = [];
  _queueMapper: QueueMapper<ResourcePayload>;

  constructor(
    queueToBodyMapper: QueueMapper<ResourcePayload>,
    flushTimeoutMs: number = DEFAULT_FLUSH_TIMEOUT_DELAY,
  ) {
    this._queueMapper = queueToBodyMapper;
    this._flushTimeoutDelay = flushTimeoutMs;
  }

  _send() {
    // To be implemented by a subclass
  }

  flush = () => {
    if (__SERVER__) {
      // can't call ResourceFactory.create on the server like this
      return;
    }
    if (this._flushTimer) {
      clearTimeout(this._flushTimer);
      this._flushTimer = null;
    }

    if (this._queue.length === 0) {
      return;
    }

    try {
      this._send();
      this._queue = [];
    } catch (e) {
      // Unable to send data. Leave the queue and try again.
    }
  };

  enqueue(data: ResourcePayload) {
    this._queue = [...this._queue, data];

    if (!this._flushTimer) {
      this._flushTimer = setTimeout(this.flush, this._flushTimeoutDelay);
    }

    if (!__SERVER__ && !this._listeningForWindowClose) {
      window.addEventListener('beforeunload', this.flush);
      this._listeningForWindowClose = true;
    }
  }
}

export default class QueuedResourceCall<ResourcePayload> extends QueuedCall<ResourcePayload> {
  _resourceName: $Keys<resources>;

  constructor(
    resourceName: $Keys<resources>,
    queueToBodyMapper: QueueMapper<ResourcePayload>,
    flushTimeoutMs: number = DEFAULT_FLUSH_TIMEOUT_DELAY,
  ) {
    super(queueToBodyMapper, flushTimeoutMs);
    this._resourceName = resourceName;
  }

  _send() {
    const resource = ResourceFactory.create(this._resourceName, this._queueMapper(this._queue));
    resource.callCreate();
  }
}

/**
 * A QueuedKeepAliveRequest is not like a QueuedResourceCall, please note the differences!
 *
 * This class is intended for logging, or fire-and-forget requests.
 *
 * Notes:
 * - By setting `keepalive: true` we signal to the browser that this request
 * should continue even if the user navigates away. Therefore this class doesn't
 * expect any response value ever.
 */
export class QueuedKeepAliveRequest<ResourcePayload> extends QueuedCall<ResourcePayload> {
  _url: string;
  _resourceName: $Keys<resources>;

  _failedKeepAlive: boolean = false; // did we fail once before?
  _stickyFallback: boolean = true; // should we keep doing the fallback once we've failed?

  constructor(
    url: string,
    resourceName: $Keys<resources>,
    queueToBodyMapper: QueueMapper<ResourcePayload>,
    flushTimeoutMs: number = DEFAULT_FLUSH_TIMEOUT_DELAY,
  ) {
    super(queueToBodyMapper, flushTimeoutMs);
    this._url = url;
    this._resourceName = resourceName;
  }

  setStickyFallback(enabled: boolean) {
    this._stickyFallback = enabled;
  }

  _shouldFallback() {
    return this._failedKeepAlive && this._stickyFallback;
  }

  _send() {
    if (this._shouldFallback()) {
      const data = this._queueMapper(this._queue);
      data.keepAlive = 'fallback';
      const resource = ResourceFactory.create(this._resourceName, data);
      resource.callCreate();
    } else {
      const Csrf = require('app/common/lib/Csrf').default;
      const data = this._queueMapper(this._queue);
      data.keepAlive = true;

      window
        .fetch(this._url, {
          method: 'POST',
          mode: 'same-origin',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRFToken': Csrf.getCSRFToken(),
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            data: {
              options: data,
            },
          }),
        })
        .catch((e) => {
          this._failedKeepAlive = true;
          data.keepAlive = 'failed';
          const resource = ResourceFactory.create(this._resourceName, data);
          resource.callCreate();

          if (process.env.NODE_ENV === 'production') {
            const logData = {
              errorObj: {
                message: '[QueuedResourceCall] ' + String(e.message),
                stack: e.stack,
                auxData: data,
              },
            };
            const numRetries = 0;
            const retryDelay = undefined;
            LogToServer.logToServer(
              LogToServer.SERVER_LOG_TYPE.ERROR,
              LogToServer.HTTP_METHOD.POST,
              logData,
              numRetries,
              retryDelay,
              Date.now(),
            );
          } else {
            // eslint-disable-next-line no-console
            console.error(
              '[QueuedKeepAliveRequest] fetch(keepAlive: true) Failed! Falling back to resource request',
            );
          }
        });
    }
  }
}
